import {
  AppealNormalFeedback,
  AppealRequest,
  CaseCreation,
  DefendantAppeal,
  HigherAdminFeedback,
  HigherAdminFeedbackRequest,
} from "types";
import apiClient from "../lib/apiClients";
import { CourtMarks, withCourtMarks } from "components/CourtFilingMarks";

// Standard paginated envelope (matches the upcoming-hearings endpoint).
export interface PageResult<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Server-side list controls for the appeal-list endpoints.
export interface AppealListParams {
  status?: string; // exact AppealRequestStatus key; omit for All
  search?: string;
  page?: number;
  pageSize?: number;
  is_cassation?: boolean; // prosecutor lists — omit for both
  is_casations?: boolean; // defendant lists (note spelling) — omit for both
}

// Accept BOTH the current bare-array response AND the future paginated
// envelope, so the frontend keeps working before/after the backend ships.
const normalizePage = <T>(data: any): PageResult<T> =>
  Array.isArray(data)
    ? { count: data.length, next: null, previous: null, results: data }
    : {
        count: data?.count ?? (data?.results?.length || 0),
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        results: data?.results ?? [],
      };

const withListParams = (url: string, params?: AppealListParams) => {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.search) q.set("search", params.search);
  if (params?.page) q.set("page", String(params.page));
  if (params?.pageSize) q.set("page_size", String(params.pageSize));
  if (params?.is_cassation !== undefined)
    q.set("is_cassation", String(params.is_cassation));
  if (params?.is_casations !== undefined)
    q.set("is_casations", String(params.is_casations));
  const qs = q.toString();
  if (!qs) return url;
  return url + (url.includes("?") ? "&" : "?") + qs;
};

// AppealRequest
export const fetchAppealRequest = async (params?: AppealListParams) => {
  const response = await apiClient.get(
    withListParams("/appeal/appeal-requests/", params),
  );
  return normalizePage<AppealRequest>(response.data);
};
export const fetchAppealRequestById = async (id: string) => {
  const response = await apiClient.get<AppealRequest>(
    "/appeal/appeal-requests/" + id + "/",
  );
  return response.data;
};
export const fetchCaseByProsecutorRegistratonNumber = async (
  prosecutionRegistrationNumber: string,
) => {
  const response = await apiClient.get<CaseCreation[]>(
    "/appeal/appeal-requests/cases-by-prosecutor/?prosecutor_registration_number=" +
      prosecutionRegistrationNumber,
  );
  return response.data;
};
export const fetchAppealForRequestingProsecutor = async (
  requesting_prosecutor_id: string,
  params?: AppealListParams,
) => {
  const response = await apiClient.get(
    withListParams(
      `appeal/appeal-requests/filter_by_requesting_prosecutor/?requesting_prosecutor_id=${requesting_prosecutor_id}`,
      params,
    ),
  );
  return normalizePage<AppealRequest>(response.data);
};
export const fetchAppealForAssignedProsecutor = async (
  requesting_prosecutor_id: string,
) => {
  const response = await apiClient.get<AppealRequest[]>(
    `appeal/appeal-requests/filter_by_assigned_prosecutor/?assigned_prosecutor_id=${requesting_prosecutor_id}`,
  );
  return response.data;
};
export const fetchAppealForRegistrar = async (
  admin_unit: string,
  params?: AppealListParams,
) => {
  const response = await apiClient.get(
    withListParams(
      `appeal/appeal-requests/filter_by_admin_unit/?admin_unit=${admin_unit}`,
      params,
    ),
  );
  return normalizePage<AppealRequest>(response.data);
};
export const fetchAppealForCoordinator = async (
  admin_unit: string,
  org_unit: string,
) => {
  const response = await apiClient.get<AppealRequest[]>(
    `appeal/appeal-requests/filter_by_admin_unit/?admin_unit=${admin_unit}&org_unit=${org_unit}`,
  );
  return response.data;
};
export const fetchAppealForCoordinatorWithType = async (
  admin_unit: string,
  role: string,
  params?: AppealListParams,
) => {
  const response = await apiClient.get(
    withListParams(
      `appeal/appeal-requests/by-admin-coordinator/?admin_unit_id=${admin_unit}&coordinator_role=${role}`,
      params,
    ),
  );
  return normalizePage<AppealRequest>(response.data);
};
export const fetchAppealForCoordinatorWithRole = async (
  admin_unit: string,
  org_unit: string,
  role: string,
) => {
  const response = await apiClient.get<AppealRequest[]>(
    `appeal/appeal-requests/filter_by_admin_unit/?admin_unit=${admin_unit}&org_unit=${org_unit}&role=${role}`,
  );
  return response.data;
};
export const fetchAppealForClerk = async (
  admin_unit: string,
  role: string,
  params?: AppealListParams,
) => {
  const response = await apiClient.get(
    withListParams(
      `appeal/appeal-requests/filter_by_crime_role_admin/?admin_unit=${admin_unit}&role=${role}`,
      params,
    ),
  );
  return normalizePage<AppealRequest>(response.data);
};
export const addAppealRequest = async (data: AppealRequest) => {
  const formData = new FormData();
  data?.defendant_ids &&
    data?.defendant_ids.forEach((file: any) => {
      formData.append("defendant_ids", file);
    });

  // Add other fields from data object if needed
  Object.entries(data).forEach(([key, value]) => {
    if (key !== "defendant_ids" && value !== undefined) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.post("/appeal/appeal-requests/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};
export const editAppealRequest = async (data: any) => {
  const formData = new FormData();
  data?.defendant_ids &&
    data?.defendant_ids.forEach((file: any) => {
      formData.append("defendant_ids", file);
    });

  // Add other fields from data object if needed
  Object.entries(data).forEach(([key, value]) => {
    if (key !== "defendant_ids" && value !== undefined) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.patch(
    "/appeal/appeal-requests/" + data?.id + "/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editAppealRequest2 = async (data: any, marks?: CourtMarks) => {
  const { body, config } = withCourtMarks(data, marks);
  const response = await apiClient.patch(
    "/appeal/appeal-requests/" + data?.id + "/",
    body,
    config,
  );
  return response.data;
};
export const deleteAppealRequest = async (reporter: AppealRequest) => {
  const response = await apiClient.delete(
    "/appeal/appeal-requests/" + reporter?.id + "/",
  );
  return response.data;
};

export const addAppealNormalFeedback = async (data: AppealNormalFeedback) => {
  const response = await apiClient.post("/appeal/normal-feedbacks/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const addAppealRespondbackFeedback = async (data: any) => {
  const response = await apiClient.post(
    "/appeal/respondback-feedbacks/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const fetchHigherAdminRequest = async () => {
  const response = await apiClient.get<HigherAdminFeedbackRequest[]>(
    `appeal/higheradmin-feedback-requests/`,
  );
  return response.data;
};
export const fetchHigherAdminRequestForCoordinator = async (
  admin_unit_id: string,
  coordinator_role: string,
) => {
  const response = await apiClient.get<HigherAdminFeedbackRequest[]>(
    `appeal/higheradmin-feedback-requests/by-admin-coordinator/?admin_unit_id=${admin_unit_id}&coordinator_role=${coordinator_role}`,
  );
  return response.data;
};
export const fetchHigherAdminRequestForRegistrar = async (
  admin_unit_id: string,
) => {
  const response = await apiClient.get<HigherAdminFeedbackRequest[]>(
    `appeal/higheradmin-feedback-requests/by-admin-unit/?admin_unit_id=${admin_unit_id}`,
  );
  return response.data;
};
export const fetchHigherAdminRequestForProsecutor = async (
  prosecutor_id: string,
) => {
  const response = await apiClient.get<HigherAdminFeedbackRequest[]>(
    `appeal/higheradmin-feedback-requests/by-prosecutor/?prosecutor_id=${prosecutor_id}`,
  );
  return response.data;
};
export const fetchHigherAdminFeedbackForCoordinator = async (
  admin_unit_id: string,
  coordinator_role: string,
) => {
  const response = await apiClient.get<HigherAdminFeedback[]>(
    `appeal/higheradmin-feedback-requests/by-admin-coordinator/?admin_unit_id/?admin_unit_id=${admin_unit_id}&coordinator_role=${coordinator_role}`,
  );
  return response.data;
};
export const fetchHigherAdminRequestById = async (id: string) => {
  const response = await apiClient.get<HigherAdminFeedbackRequest>(
    "/appeal/higheradmin-feedback-requests/" + id + "/",
  );
  return response.data;
};
export const addHigherAdminFeedbackRequest = async (data: any) => {
  const response = await apiClient.post(
    "/appeal/higheradmin-feedback-requests/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editHigherAdminFeedbackRequest = async (data: any) => {
  const response = await apiClient.patch(
    "/appeal/higheradmin-feedback-requests/" + data?.id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editHigherAdminFeedbackRequest2 = async (data: any) => {
  const response = await apiClient.patch(
    "/appeal/higheradmin-feedback-requests/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteHigherAdminFeedbackRequest = async (data: any) => {
  const response = await apiClient.delete(
    "/appeal/higheradmin-feedback-requests/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const addHigherAdminRespondbackFeedback = async (data: any) => {
  const response = await apiClient.post(
    "/appeal/higheradmin-respondback-feedbacks/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const addAppealPreJudgment = async (data: any) => {
  const response = await apiClient.post(
    "/appeal/appeal_prejudgement-entries/",
    data,
  );
  return response.data;
};

export const addAppealDailyReport = async (data: any) => {
  const response = await apiClient.post("/appeal/appeal_daily-reports/", data);
  return response.data;
};

export const addAppealDailyReportFeedback = async (data: any) => {
  const response = await apiClient.post(
    "/appeal/feedback-on-daily-reports/",
    data,
  );
  return response.data;
};

export const addAppealFinalVerdict = async (data: any) => {
  // When a court-decision file is attached, send as multipart (the backend
  // tags court_decision_file with document_type final_verdict_court_decision).
  const hasFile = data?.court_decision_file instanceof File;
  if (!hasFile) {
    const { court_decision_file, ...rest } = data || {};
    const response = await apiClient.post(
      "/appeal/appeal-final-verdicts/",
      rest,
    );
    return response.data;
  }

  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key === "court_decision_file" && value instanceof File) {
      formData.append("court_decision_file", value);
    } else if (Array.isArray(value)) {
      value.forEach((v) => formData.append(key, String(v)));
    } else {
      formData.append(key, String(value));
    }
  });
  const response = await apiClient.post(
    "/appeal/appeal-final-verdicts/",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

// Upload the court-decision file to an existing final verdict (after creation).
// The backend tags court_decision_file with document_type
// final_verdict_court_decision.
export const uploadAppealFinalVerdictDecision = async (
  id: number | string,
  file: File,
) => {
  const formData = new FormData();
  formData.append("court_decision_file", file);
  const response = await apiClient.patch(
    `/appeal/appeal-final-verdicts/${id}/`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

// DEFENDANT APPEAL
export const fetchDefendantAppealRequest = async (params?: AppealListParams) => {
  const response = await apiClient.get(
    withListParams("/appeal/defendant-appeal-requests/", params),
  );
  return normalizePage<DefendantAppeal>(response.data);
};
export const fetchDefendantAppealRequestByAdminCoordinator = async (
  admin_unit: string,
  role: string,
  params?: AppealListParams,
) => {
  const response = await apiClient.get(
    withListParams(
      `/appeal/defendant-appeal-requests/by-admin-coordinator/?admin_unit_id=${admin_unit}&coordinator_role=${role}`,
      params,
    ),
  );
  return normalizePage<DefendantAppeal>(response.data);
};
export const fetchDefendantAppealRequestByAssignedProsecutor = async (
  assigned_prosecutor_id: string,
  params?: AppealListParams,
) => {
  const response = await apiClient.get(
    withListParams(
      `/appeal/defendant-appeal-requests/by-assigned-prosecutor/?assigned_prosecutor_id=${assigned_prosecutor_id}`,
      params,
    ),
  );
  return normalizePage<DefendantAppeal>(response.data);
};
export const fetchDefendantAppealById = async (id: string) => {
  const response = await apiClient.get<DefendantAppeal>(
    "/appeal/defendant-appeal-requests/" + id + "/",
  );
  return response.data;
};
export const addDefendantAppeal = async (data: DefendantAppeal) => {
  const formData = new FormData();
  data?.defendant_ids &&
    data?.defendant_ids.forEach((file: any) => {
      formData.append("defendant_ids", file);
    });

  // Add other fields from data object if needed
  Object.entries(data).forEach(([key, value]) => {
    if (key !== "defendant_ids" && value !== undefined) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.post(
    "/appeal/defendant-appeal-requests/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editDefendantAppeal = async (data: any) => {
  const formData = new FormData();
  data?.defendant_ids &&
    data?.defendant_ids.forEach((file: any) => {
      formData.append("defendant_ids", file);
    });

  // Add other fields from data object if needed
  Object.entries(data).forEach(([key, value]) => {
    if (key !== "defendant_ids" && value !== undefined) {
      formData.append(key, value as any);
    }
  });

  const response = await apiClient.patch(
    "/appeal/defendant-appeal-requests/" + data?.id + "/",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editDefendantAppeal2 = async (data: any) => {
  const response = await apiClient.patch(
    "/appeal/defendant-appeal-requests/" + data?.id + "/",
    data,
  );
  return response.data;
};
